import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, Loader2, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * Right-side slide-out drawer used to preview ad creatives without leaving
 * the report. Handles a few important edge cases:
 *
 * - YouTube / Vimeo `watch` URLs are rewritten to their embeddable form so
 *   the inline player loads.
 * - AdPiler URLs are detected up front and shown as a "metadata + Open in
 *   AdPiler" card immediately (AdPiler blocks iframe embedding via
 *   X-Frame-Options, so attempting an iframe just produces 8s of blank
 *   spinner). Same pattern works for any host known to block embedding.
 * - For any other URL we attempt the iframe, then fall back to an external
 *   "Open in New Tab" card if it doesn't fire `onLoad` within 5s.
 */
export default function AdPreviewModal({ creative, onClose }) {
  const [loaded, setLoaded] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const url = creative?.url || "";

  // Classify the URL host so we can pick the right rendering path. We try
  // to iframe everything by default (AdPiler, generic web pages, etc.) and
  // only special-case YouTube/Vimeo to rewrite their watch URLs into
  // embeddable form. If the iframe never fires onLoad within 8s, we
  // gracefully fall back to an "Open in New Tab" card.
  const kind = useMemo(() => {
    if (!url) return "none";
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, "");
      if (host === "youtu.be" || host.endsWith("youtube.com")) return "youtube";
      if (host === "vimeo.com" || host.endsWith(".vimeo.com")) return "vimeo";
      return "generic";
    } catch {
      return "generic";
    }
  }, [url]);

  // Convert known watch URLs into embed URLs. Pass-through everything else.
  const embedUrl = useMemo(() => {
    if (!url) return "";
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, "");
      if (host === "youtu.be") {
        const id = u.pathname.replace(/^\//, "").split("/")[0];
        return id ? `https://www.youtube.com/embed/${id}` : url;
      }
      if (host.endsWith("youtube.com")) {
        const v = u.searchParams.get("v");
        if (v) return `https://www.youtube.com/embed/${v}`;
        const m = u.pathname.match(/^\/(?:shorts|embed)\/([^/]+)/);
        if (m) return `https://www.youtube.com/embed/${m[1]}`;
        return url;
      }
      if ((host === "vimeo.com" || host.endsWith(".vimeo.com")) && host !== "player.vimeo.com") {
        const parts = u.pathname.replace(/^\//, "").split("/").filter(Boolean);
        if (parts[0] && /^\d+$/.test(parts[0])) {
          const id = parts[0];
          const h = parts[1];
          return h ? `https://player.vimeo.com/video/${id}?h=${h}` : `https://player.vimeo.com/video/${id}`;
        }
      }
      return url;
    } catch {
      return url;
    }
  }, [url]);

  useEffect(() => {
    setLoaded(false);
    setBlocked(false);
    // Give the iframe 8s to fire onLoad. If it doesn't (because the host
    // sets X-Frame-Options or CSP frame-ancestors), fall back to the
    // "Open in New Tab" card.
    const t = setTimeout(() => setBlocked(true), 8000);
    return () => clearTimeout(t);
  }, [url]);

  // Friendly host label for the external CTA
  const hostLabel = useMemo(() => {
    if (!url) return "Source";
    try {
      const h = new URL(url).hostname.replace(/^www\./, "");
      if (h === "youtu.be" || h.endsWith("youtube.com")) return "YouTube";
      if (h === "vimeo.com" || h.endsWith(".vimeo.com")) return "Vimeo";
      return "New Tab";
    } catch {
      return "Source";
    }
  }, [url]);

  if (!creative) return null;

  const showIframe = !blocked || loaded;
  const showExternalCard = blocked && !loaded;

  return (
    <AnimatePresence>
      {/* Backdrop - click to close */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="ad-preview-backdrop"
      />

      {/* Drawer panel */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="fixed top-0 right-0 bottom-0 z-[101] w-[50%] max-w-[960px] bg-white shadow-2xl flex flex-col"
        data-testid="ad-preview-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-violet-500/20 bg-gradient-to-r from-slate-800 via-violet-900/40 to-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0 mr-4">
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              className="text-slate-400 hover:text-white hover:bg-white/10 h-8 px-2 flex-shrink-0"
              data-testid="ad-preview-close-btn"
            >
              <ChevronRight className="w-5 h-5" />
              <span className="ml-1 text-xs">Back to Report</span>
            </Button>
            <div className="min-w-0">
              <p className="font-semibold text-white text-sm truncate">
                {creative.label}
              </p>
              {creative.notes && (
                <p className="text-xs text-slate-400 truncate">{creative.notes}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-violet-400 hover:text-violet-300 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="ad-preview-open-external"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open in New Tab
            </a>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              className="text-slate-400 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 relative">
          {/* Loading spinner shown while iframe is initializing */}
          {showIframe && !loaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 z-10">
              <Loader2 className="w-8 h-8 animate-spin text-violet-400 mb-3" />
              <p className="text-sm text-slate-400">Loading preview...</p>
            </div>
          )}

          {/* External-card view (host blocks iframe) */}
          {showExternalCard && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 z-10 px-8 text-center">
              <p className="text-slate-600 text-sm mb-1 max-w-md">
                This preview couldn't load inline. You can view it directly:
              </p>
              <p className="text-slate-400 text-xs mb-5 truncate max-w-md">{url}</p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-6 py-2.5 rounded-xl transition-all"
                data-testid="ad-preview-external-cta"
              >
                <ExternalLink className="w-4 h-4" />
                Open in {hostLabel}
              </a>
            </div>
          )}

          {/* Iframe — only mounted for hosts we expect to embed cleanly */}
          {showIframe && (
            <iframe
              key={embedUrl}
              src={embedUrl}
              title={creative.label}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
              allowFullScreen
              onLoad={() => setLoaded(true)}
              data-testid="ad-preview-iframe"
            />
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
